<template>
    <Dialog :header="header" :visible.sync="visible" class="dialog-container custom-dialog" modal :style="{ width: '30rem' }" :closable="false">
        <template #header>
            <div class="header-container">
                <span class="title-dialog">{{ header }}</span>
            </div>
        </template>
        <div class="form-container mt-3">
            <FormKit
                v-model="localSelectedMethod"
                type="select"
                id="methodSelect"
                :options="[
                    { value: 'IP', label: 'Par IP' },
                    { value: 'Company', label: 'Par société' },
                    { value: 'Email', label: 'Par email' }
                  ]"
            />
            <FormKit
                type="form"
                id="supplierForm"
                @submit="submitForm"
                :actions="false"
                incomplete-message="Désolé, tous les champs ne sont pas remplis correctement."
                #default="{ state: { valid } }"
            >
                <template v-if="localSelectedMethod === 'IP'">
                    <div :class="['form-field', {'invalid-field': !formData.IP.address}]">
                        <FormKit
                            type="text"
                            name="ipAddress"
                            label="Adresse IP*"
                            v-model="formData.IP.address"
                            :validation-rules="{ validateIpAddress }"
                            validation="required|validateIpAddress"
                            :validation-messages="{
                                  required: 'Veuillez entrer une adresse IP.',
                                  validateIpAddress: validateIpAddressError,
                                }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.IP.company}]">
                        <FormKit
                            type="autocomplete"
                            label="Société*"
                            name="company"
                            placeholder="Sélectionner une société"
                            :options="searchSupplierOptions"
                            v-model="formData.IP.company"
                            validation="required"
                            :validation-messages="{ required: 'Veuillez sélectionner une société.' }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.IP.requestedBy}]">
                        <FormKit
                            type="text"
                            v-model="formData.IP.requestedBy"
                            label="Personne qui fait la demande*"
                            validation="required"
                            :validation-messages="{ required: 'Veuillez entrer la personne qui fait la demande.' }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.IP.description}]">
                        <FormKit
                            type="textarea"
                            v-model="formData.IP.description"
                            label="Description*"
                            validation="required|length:0,255"
                            :validation-messages="{ 
                                required: 'Veuillez entrer une description.',
                                length: 'Ce champ doit contenir maximum 255 caractères.',
                            }"
                        />
                    </div>
                </template>

                <template v-if="localSelectedMethod === 'Company'">
                    <div :class="['form-field', {'invalid-field': !formData.Company.companyName }]">
                        <FormKit
                            type="autocomplete"
                            label="Nom de la société*"
                            name="company"
                            placeholder="Sélectionner une société"
                            :options="searchSupplierOptions"
                            v-model="formData.Company.companyName"
                            validation="required|validateCompanyNotInList"
                            :validation-rules="{validateCompanyNotInList}"
                            :validation-messages="{ 
                                    required: 'Veuillez sélectionner une société.',
                                    validateCompanyNotInList: 'Cette société existe déjà.'
                                }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.Company.requestedBy}]">
                        <FormKit
                            type="text"
                            v-model="formData.Company.requestedBy"
                            label="Personne qui fait la demande*"
                            validation="required"
                            :validation-messages="{ required: 'Veuillez entrer la personne qui fait la demande.' }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.Company.description}]">
                        <FormKit
                            type="textarea"
                            v-model="formData.Company.description"
                            label="Description*"
                            validation="required|length:0,255"
                            :validation-messages="{ 
                                required: 'Veuillez entrer une description.',
                                length: 'Ce champ doit contenir maximum 255 caractères.',
                            }"
                        />
                    </div>
                </template>

                <template v-if="localSelectedMethod === 'Email'">
                    <div :class="['form-field', {'invalid-field': !formData.Email.email}]">
                        <FormKit
                            type="email"
                            v-model="formData.Email.email"
                            label="Email*"
                            validation="required|email"
                            :validation-messages="{
                                required: 'Veuillez entrer un email.',
                                email: 'Veuillez entrer un email valide.'
                            }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.Email.requestedBy}]">
                        <FormKit
                            type="text"
                            v-model="formData.Email.requestedBy"
                            label="Personne qui fait la demande*"
                            validation="required"
                            :validation-messages="{ required: 'Veuillez entrer la personne qui fait la demande.' }"
                        />
                    </div>
                    <div :class="['form-field', {'invalid-field': !formData.Email.description}]">
                        <FormKit
                            type="textarea"
                            v-model="formData.Email.description"
                            label="Description*"
                            validation="required|length:0,255"
                            :validation-messages="{ 
                                required: 'Veuillez entrer une description.',
                                length: 'Ce champ doit contenir maximum 255 caractères.',
                            }"
                        />
                    </div>
                </template>

                <div class="button-group flex justify-between w-full px-4">
                    <Button type="submit" label="Ajouter" class="mr-3 flex-grow bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" />
                    <Button type="button" label="Retour" class="p-button-danger" @click="closeDialog" />
                </div>
            </FormKit>
        </div>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, reactive, watch, defineEmits, defineProps } from 'vue';
import { getPaginatedCompanyForDropDown } from '@/_services/company/company';
import { CompanyWhitelist } from '@/models/securite/whitelist/IcompanyWhiteLis';
interface Props {
    header: string;
    visible: boolean;
    selectedMethod: string;
    tableData: CompanyWhitelist[];
    isSubmitting: boolean
}
const props = defineProps<Props>();
const emit = defineEmits(['confirm', 'update:visible', 'update:selected-method']);
const localSelectedMethod = ref(props.selectedMethod);
const validateIpAddressError = 'Veuillez entrer une adresse IP valide.';
function validateIpAddress (node) {
    const value = node.value;
    const regex = new RegExp(`^\\d+(\\.\\d+)+$`);
    return regex.test(value);
}
const formData = reactive({
    IP: {
        address: '',
        company: '',
        description: '',
        requestedBy: ''
    },
    Company: {
        companyName: '',
        description: '',
        requestedBy: ''
    },
    Email: {
        email: '',
        description: '',
        requestedBy: ''
    }
});
const validateCompanyNotInList = (node) => {
    const isInCompany =  props.tableData.some(item => {
        return item.company.uuid === node.value;
    });

    return !isInCompany
};
const submitForm = async () => {
    const data = formData[localSelectedMethod.value];
    if (!validateFormData(data)) {
        return;
    }
    try {
        emit('confirm', data);
    } catch (error) {
        console.error('Error during form submission:', error);
    } finally {
        //props.isSubmitting = false; 
    }
};


const validateFormData = (data: any) => {
    let isValid = true;
    for (const key in data) {
        if (data[key] === '') {
            isValid = false;
        }
    }
    return isValid;
};

const closeDialog = () => {
    emit('update:visible', false);
    resetFormData();
};

const resetFormData = () => {
    formData.IP.address = '';
    formData.IP.company = '';
    formData.IP.description = '';
    formData.IP.requestedBy = '';
    formData.Company.companyName = '';
    formData.Company.description = '';
    formData.Company.requestedBy = '';
    formData.Email.email = '';
    formData.Email.description = '';
    formData.Email.requestedBy = '';
};

watch(() => props.visible, (newVal) => {
    if (!newVal) {
        resetFormData();
    }
});

watch(localSelectedMethod, (newVal) => {
    emit('update:selected-method', newVal);
});

async function searchSupplierOptions({ search, page, hasNextPage }) {
    const [totalCompaniesRecordsForDropDown, companiesForDropDown] = await getPaginatedCompanyForDropDown(5, page, search, true);

    const hasItems = companiesForDropDown.length > 0;
    if (hasItems && page !== totalCompaniesRecordsForDropDown) {
        hasNextPage();
    }

    return companiesForDropDown;
}
</script>

<style scoped>
.dialog-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}

.custom-dialog {
    width: 100%;
    max-width: 600px;
    padding: 20px;
    box-sizing: border-box;
}

.form-container {
    width: 100%;
}

.form-field, .button-group {
    width: 100%;
    margin-bottom: 10px;
}

input, select, textarea, .button-group button {
    width: 100%;
    padding: 10px;
    box-sizing: border-box;
}

.title-dialog {
    font-size: 20px;
    text-transform: uppercase;
    color: #0A3382;
    position: relative;
}

.title-dialog:after {
    content: "";
    background: #f5a22e;
    width: 35px;
    height: 3px;
    position: absolute;
    bottom: -5px;
    left: 0;
    display: block;
}
</style>
